import React from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Button,
  Collapse,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import { BiTrash, BiPlus, BiCheck, BiX, BiPencil, BiShow, BiArrowBack, BiUndo } from 'react-icons/bi';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { toast } from 'react-toastify';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';
import type { Businesshours_Insert_Input } from '@gql/base-types';
import {
  DAY_NAMES,
  timeToDbFormat,
  dbTimeTodayjs,
  formatTimeRange,
  validateTimeRange,
  mergeTimeBlocks,
} from '@features/settings/utils/businessHoursUtils';
import type {
  ScheduleEditorProps,
  DayHours,
  TimeBlock,
  DayOverride,
} from './ScheduleEditor.types';
import WeeklyTimeline from './WeeklyTimeline';

// Sunday-first ordering (US standard)
const WEEK_ORDER = [0, 1, 2, 3, 4, 5, 6];
const DAY_ABBREVS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Preset quick-set options
const PRESETS = [
  { label: '9 AM – 5 PM', open: '09:00:00', close: '17:00:00' },
  { label: '8 AM – 4 PM', open: '08:00:00', close: '16:00:00' },
  { label: '7 AM – 3 PM', open: '07:00:00', close: '15:00:00' },
  { label: '10 AM – 6 PM', open: '10:00:00', close: '18:00:00' },
];

// Weekday quick-select groups
const QUICK_GROUPS = [
  { label: 'Weekdays', days: [1, 2, 3, 4, 5] },
  { label: 'Weekends', days: [0, 6] },
  { label: 'All', days: [0, 1, 2, 3, 4, 5, 6] },
];

/**
 * Schedule Editor — visual day-grid with multi-select and edit panel.
 *
 * Inspired by the driver-app calendar grid UX but adapted for recurring
 * weekly schedules. Shows a row of 7 day cells (Sun-Sat) with color-coded
 * availability status. Click days to select (multi-select supported),
 * then configure via the edit panel below. Changes are batched and saved
 * explicitly.
 */
const ScheduleEditor: React.FC<ScheduleEditorProps> = ({
  config,
  scheduleData,
  onCreate,
  onUpdate,
  onDelete,
  loading,
  userName,
  currentWeekOverrides,
  onRefetch,
  onManageOverrides,
}) => {
  const { entityId, timezone, labels, types, naming } = config;

  const [dayHours, setDayHours] = React.useState<DayHours[]>([]);
  const [originalHours, setOriginalHours] = React.useState<DayHours[]>([]);
  const [selectedDays, setSelectedDays] = React.useState<Set<number>>(new Set());
  const [saving, setSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isPreviewing, setIsPreviewing] = React.useState(false);
  const [isCustomMultiSelect, setIsCustomMultiSelect] = React.useState(false);

  // The "edit template" — time blocks shown in the edit panel.
  // When editing multiple days, this is the shared configuration applied to all.
  const [editBlocks, setEditBlocks] = React.useState<TimeBlock[]>([]);
  const [editIsClosed, setEditIsClosed] = React.useState(true);

  // Process schedule data based on entity type
  const processScheduleData = React.useCallback(() => {
    const specific = scheduleData.filter((h) => {
      if (config.entityType === 'driver') {
        return h.driver_id?.toString() === entityId && h.type === types.specific;
      }
      return h.location_id?.toString() === entityId && h.type === types.specific;
    });

    const defaults = scheduleData.filter((h) => {
      if (config.entityType === 'driver') {
        return !h.driver_id && h.type === types.default;
      }
      return !h.location_id && h.type === types.default;
    });

    const initialState: DayHours[] = DAY_NAMES.map((_dayName, index) => {
      // NOTE: `entityHours` keeps ALL specific rows for the day, including any
      // `is_open=false` "closed marker" rows. The marker's job is to record that
      // this day is *explicitly* closed so it does NOT fall back to default-hours
      // below. But a marker is not a real time block, so we only surface
      // `is_open=true` rows as editable blocks. (Rows with `is_open=false` and
      // stale times used to leak through here as ghost/overlapping blocks — that
      // was the bug.)
      const entityHours = specific.filter((h) => h.day_of_week === index);

      if (entityHours.length > 0) {
        return {
          dayOfWeek: index,
          timeBlocks: entityHours
            .filter((h) => h.is_open)
            .map((block) => ({
              id: block.id.toString(),
              openTime: dbTimeTodayjs(block.open_time, timezone),
              closedTime: dbTimeTodayjs(block.closed_time, timezone),
              isDefault: false,
            })),
          isClosed: !entityHours.some((h) => h.is_open),
        };
      }

      const defaultHours = defaults.filter((h) => h.day_of_week === index);

      if (defaultHours.length > 0) {
        return {
          dayOfWeek: index,
          timeBlocks: defaultHours
            .filter((h) => h.is_open)
            .map((block) => ({
              id: block.id.toString(),
              openTime: dbTimeTodayjs(block.open_time, timezone),
              closedTime: dbTimeTodayjs(block.closed_time, timezone),
              isDefault: true,
            })),
          isClosed: !defaultHours.some((h) => h.is_open),
        };
      }

      return {
        dayOfWeek: index,
        timeBlocks: [],
        isClosed: true,
      };
    });

    return initialState;
  }, [scheduleData, entityId, types.specific, types.default, timezone, config.entityType]);

  // Initialize form state and store original snapshot for change detection
  React.useEffect(() => {
    if (scheduleData.length >= 0 && entityId) {
      const processed = processScheduleData();
      setDayHours(processed);

      const originalCopy: DayHours[] = processed.map((day) => ({
        ...day,
        timeBlocks: day.timeBlocks.map((block) => ({
          ...block,
          openTime: block.openTime,
          closedTime: block.closedTime,
        })),
      }));
      setOriginalHours(originalCopy);
      setHasChanges(false);
    }
  }, [scheduleData, entityId, processScheduleData]);

  // Sync edit panel state when selection changes
  React.useEffect(() => {
    if (selectedDays.size === 0) return;

    // Use the first selected day's state as the edit template
    const firstDay = dayHours.find((d) => selectedDays.has(d.dayOfWeek));
    if (firstDay) {
      setEditIsClosed(firstDay.isClosed);
      setEditBlocks(
        firstDay.isClosed || firstDay.timeBlocks.length === 0
          ? [{ openTime: dbTimeTodayjs('09:00:00', timezone), closedTime: dbTimeTodayjs('17:00:00', timezone), isDefault: false }]
          : firstDay.timeBlocks.map((b) => ({
              ...b,
              // Keep IDs when single day — they'll flow through applyToSelected
              id: selectedDays.size === 1 ? b.id : undefined,
              isDefault: false,
            }))
      );
    }
  // Only re-sync when the set of selected days changes, not on every dayHours update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays]);

  // Track which days have pending (unsaved) changes
  const pendingDays = React.useMemo(() => {
    const pending = new Set<number>();
    for (const day of dayHours) {
      const original = originalHours.find((d) => d.dayOfWeek === day.dayOfWeek);
      if (!original) continue;
      if (day.isClosed !== original.isClosed) { pending.add(day.dayOfWeek); continue; }
      if (day.timeBlocks.length !== original.timeBlocks.length) { pending.add(day.dayOfWeek); continue; }
      for (let i = 0; i < day.timeBlocks.length; i++) {
        const curr = day.timeBlocks[i];
        const orig = original.timeBlocks[i];
        if (!orig) { pending.add(day.dayOfWeek); break; }
        const currOpen = curr.openTime ? timeToDbFormat(curr.openTime, timezone) : null;
        const origOpen = orig.openTime ? timeToDbFormat(orig.openTime, timezone) : null;
        const currClose = curr.closedTime ? timeToDbFormat(curr.closedTime, timezone) : null;
        const origClose = orig.closedTime ? timeToDbFormat(orig.closedTime, timezone) : null;
        if (currOpen !== origOpen || currClose !== origClose) { pending.add(day.dayOfWeek); break; }
      }
    }
    return pending;
  }, [dayHours, originalHours, timezone]);

  // --- Day grid click handler ---
  const handleDayClick = (dayIndex: number) => {
    if (isCustomMultiSelect) {
      // Custom multi-select: toggle individual days
      setSelectedDays((prev) => {
        const next = new Set(prev);
        if (next.has(dayIndex)) {
          next.delete(dayIndex);
        } else {
          next.add(dayIndex);
        }
        return next;
      });
    } else {
      // Default: exclusive single-day selection
      setSelectedDays((prev) => {
        if (prev.size === 1 && prev.has(dayIndex)) {
          return new Set(); // Deselect if clicking same day
        }
        return new Set([dayIndex]);
      });
    }
  };

  // --- Timeline row click: enter edit mode with that day preselected ---
  const handleTimelineRowClick = (dayIndex: number) => {
    setIsEditing(true);
    setIsCustomMultiSelect(false);
    setSelectedDays(new Set([dayIndex]));
  };

  // --- Quick group select (exclusive — replaces current selection) ---
  const handleGroupSelect = (days: number[]) => {
    setIsCustomMultiSelect(false);
    setSelectedDays(new Set(days));
  };

  // --- Reset a single day's changes back to original ---
  const handleResetDay = (dayIndex: number) => {
    const original = originalHours.find((d) => d.dayOfWeek === dayIndex);
    if (!original) return;
    setDayHours((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayIndex
          ? { ...original, timeBlocks: original.timeBlocks.map((b) => ({ ...b })) }
          : day
      )
    );
    // Re-sync edit panel if this day is selected
    if (selectedDays.has(dayIndex)) {
      const firstDay = originalHours.find((d) => d.dayOfWeek === dayIndex);
      if (firstDay) {
        setEditIsClosed(firstDay.isClosed);
        setEditBlocks(
          firstDay.isClosed || firstDay.timeBlocks.length === 0
            ? [{ openTime: dbTimeTodayjs('09:00:00', timezone), closedTime: dbTimeTodayjs('17:00:00', timezone), isDefault: false }]
            : firstDay.timeBlocks.map((b) => ({ ...b, id: b.id, isDefault: false }))
        );
      }
    }
  };

  // --- Apply edit state to all selected days ---
  const applyToSelected = React.useCallback(
    (isClosed: boolean, blocks: TimeBlock[]) => {
      setDayHours((prev) =>
        prev.map((day) => {
          if (!selectedDays.has(day.dayOfWeek)) return day;
          return {
            ...day,
            isClosed,
            timeBlocks: isClosed
              ? day.timeBlocks // preserve existing blocks in data, just mark closed
              : blocks.map((b, idx) => ({
                  // Preserve existing block ID when editing a single day
                  id: selectedDays.size === 1 ? (b.id ?? day.timeBlocks[idx]?.id) : undefined,
                  openTime: b.openTime,
                  closedTime: b.closedTime,
                  isDefault: false,
                })),
          };
        })
      );
      setHasChanges(true);
    },
    [selectedDays]
  );

  // --- Edit panel handlers (modify template + push to selected days) ---
  const handleSetAvailable = () => {
    setEditIsClosed(false);
    const blocks = editBlocks.length === 0
      ? [{ openTime: dbTimeTodayjs('09:00:00', timezone), closedTime: dbTimeTodayjs('17:00:00', timezone), isDefault: false }]
      : editBlocks;
    setEditBlocks(blocks);
    applyToSelected(false, blocks);
  };

  const handleSetUnavailable = () => {
    setEditIsClosed(true);
    applyToSelected(true, editBlocks);
  };

  const handleApplyPreset = (open: string, close: string) => {
    const blocks: TimeBlock[] = [{
      openTime: dbTimeTodayjs(open, timezone),
      closedTime: dbTimeTodayjs(close, timezone),
      isDefault: false,
    }];
    setEditIsClosed(false);
    setEditBlocks(blocks);
    applyToSelected(false, blocks);
  };

  const handleAddTimeBlock = () => {
    let newOpen: string;
    let newClose: string;

    if (editBlocks.length > 0) {
      const lastBlock = editBlocks[editBlocks.length - 1];
      const lastEndDb = lastBlock.closedTime ? timeToDbFormat(lastBlock.closedTime, timezone) : null;
      if (lastEndDb) {
        const [hh, mm] = lastEndDb.split(':').map(Number);
        const startHour = Math.min(hh + 1, 23);
        const endHour = Math.min(startHour + 4, 23);
        const endMin = endHour === 23 ? 59 : mm;
        newOpen = `${String(startHour).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
        newClose = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}:00`;
      } else {
        newOpen = '09:00:00';
        newClose = '13:00:00';
      }
    } else {
      newOpen = '09:00:00';
      newClose = '13:00:00';
    }

    const newBlocks = [
      ...editBlocks,
      {
        openTime: dbTimeTodayjs(newOpen, timezone),
        closedTime: dbTimeTodayjs(newClose, timezone),
        isDefault: false,
      },
    ];
    setEditBlocks(newBlocks);
    applyToSelected(false, newBlocks);
  };

  const handleDeleteTimeBlock = (blockIndex: number) => {
    const newBlocks = editBlocks.filter((_, idx) => idx !== blockIndex);
    if (newBlocks.length === 0) {
      // Removing the last block switches to unavailable
      setEditBlocks([]);
      setEditIsClosed(true);
      applyToSelected(true, []);
    } else {
      setEditBlocks(newBlocks);
      applyToSelected(false, newBlocks);
    }
  };

  const handleTimeBlockChange = (
    blockIndex: number,
    field: 'openTime' | 'closedTime',
    value: any
  ) => {
    const newBlocks = editBlocks.map((block, idx) => {
      if (idx !== blockIndex) return block;
      const updated = { ...block, [field]: value, isDefault: false };

      // When openTime changes, auto-bump closedTime if it's now at or before the new open
      if (field === 'openTime' && value && updated.closedTime) {
        if (value.isSame(updated.closedTime) || value.isAfter(updated.closedTime)) {
          const newClose = value.add(4, 'hour');
          // Cap at 23:59
          updated.closedTime = newClose.hour() < value.hour()
            ? value.set('hour', 23).set('minute', 59)
            : newClose;
        }
      }

      return updated;
    });
    setEditBlocks(newBlocks);
    applyToSelected(false, newBlocks);
  };

  // --- Save (batched) ---
  const handleSave = async () => {
    for (const day of dayHours) {
      if (!day.isClosed) {
        for (const block of day.timeBlocks) {
          if (!block.isDefault) {
            const validation = validateTimeRange(block.openTime, block.closedTime);
            if (!validation.valid) {
              toast.error(`${DAY_NAMES[day.dayOfWeek]}: ${validation.error}`);
              return;
            }
          }
        }
      }
    }

    // Collapse any overlapping blocks per day just before persisting. We merge
    // here (at save) rather than while editing so the live edit state — and its
    // index-based id matching in `applyToSelected` / `pendingDays` — stays
    // untouched. The mutation diff below still compares against the UNMERGED
    // `originalHours` snapshot, so create/update/delete stay correct: the
    // surviving merged block keeps one original id (→ UPDATE) and the blocks it
    // absorbed lose theirs (→ DELETE via the "removed blocks" branch).
    const effectiveDayHours = dayHours.map((d) =>
      d.isClosed ? d : { ...d, timeBlocks: mergeTimeBlocks(d.timeBlocks, timezone) }
    );

    setSaving(true);

    try {
      // Phase 1 — Collect all mutations
      const toCreate: Businesshours_Insert_Input[] = [];
      const toUpdate: { id: string; input: any }[] = [];
      const toDelete: { id: string }[] = [];

      const entityField = config.entityType === 'driver'
        ? { driver_id: Number(entityId) }
        : { location_id: Number(entityId) };

      for (const day of effectiveDayHours) {
        const originalDay = originalHours.find((d) => d.dayOfWeek === day.dayOfWeek);

        const isClosedChanged = day.isClosed !== originalDay?.isClosed;
        if (isClosedChanged && originalDay) {
          const isNowOpen = !day.isClosed;
          const existingBlocks = originalDay.timeBlocks.filter((b) => b.id && !b.isDefault);

          // Remove all old blocks for this day. Both entity types hard-delete
          // now — the old location "soft-delete" (is_open=false with times kept)
          // is what caused deleted blocks to resurface as ghost/overlapping rows.
          for (const block of existingBlocks) {
            if (block.id) {
              toDelete.push({ id: block.id });
            }
          }

          if (isNowOpen) {
            // Reopening a location day: hard-delete leaves the day's rows empty,
            // but a lingering `is_open=false` closed-marker row (written when the
            // day was closed) is NOT in originalDay.timeBlocks (markers are
            // filtered out in processScheduleData). Sweep it from the raw
            // scheduleData so markers don't accumulate across close/open toggles.
            if (config.entityType === 'location') {
              scheduleData
                .filter(
                  (h) =>
                    h.location_id?.toString() === entityId &&
                    h.type === types.specific &&
                    h.day_of_week === day.dayOfWeek &&
                    !h.is_open
                )
                .forEach((h) => toDelete.push({ id: h.id.toString() }));
            }

            // Create new blocks from the current edit panel state
            const currentBlocks = day.timeBlocks.filter((b) => !b.isDefault);
            for (const block of currentBlocks) {
              toCreate.push({
                ...entityField,
                day_of_week: day.dayOfWeek,
                type: types.specific,
                is_open: true,
                open_time: timeToDbFormat(block.openTime, timezone),
                closed_time: timeToDbFormat(block.closedTime, timezone),
                name: naming(DAY_NAMES[day.dayOfWeek]),
                created_by: userName,
                created_at: 'now()',
                updated_by: userName,
                updated_at: 'now()',
              });
            }
          } else if (config.entityType === 'location' || existingBlocks.length === 0) {
            // Closing a day: write ONE is_open=false, null-times "closed marker"
            // row. This may be unintuitive, but processScheduleData falls
            // back to shared default-hours when a day has ZERO specific rows, so
            // for locations (which we now hard-delete) we must leave a marker
            // behind, otherwise a closed day would silently revert to default open hours.
            // Drivers keep the original behavior (only write a marker when there
            // were no rows to delete).
            toCreate.push({
              ...entityField,
              day_of_week: day.dayOfWeek,
              type: types.specific,
              is_open: false,
              open_time: null,
              closed_time: null,
              name: naming(DAY_NAMES[day.dayOfWeek]),
              created_by: userName,
              created_at: 'now()',
              updated_by: userName,
              updated_at: 'now()',
            });
          }
          continue;
        }

        if (!day.isClosed) {
          const currentBlocks = day.timeBlocks.filter((b) => !b.isDefault);
          const originalBlocks = originalDay?.timeBlocks || [];

          // Blocks to create
          const newBlocks = currentBlocks.filter((b) => {
            if (!b.id) return true;
            const originalBlock = originalBlocks.find((orig) => orig.id === b.id);
            return originalBlock?.isDefault === true;
          });
          for (const block of newBlocks) {
            toCreate.push({
              ...entityField,
              day_of_week: day.dayOfWeek,
              type: types.specific,
              is_open: true,
              open_time: timeToDbFormat(block.openTime, timezone),
              closed_time: timeToDbFormat(block.closedTime, timezone),
              name: naming(DAY_NAMES[day.dayOfWeek]),
              created_by: userName,
              created_at: 'now()',
              updated_by: userName,
              updated_at: 'now()',
            });
          }

          // Blocks to update
          for (const currentBlock of currentBlocks) {
            if (currentBlock.id) {
              const originalBlock = originalBlocks.find((b) => b.id === currentBlock.id);
              if (originalBlock && !originalBlock.isDefault) {
                const currentOpenTime = currentBlock.openTime ? timeToDbFormat(currentBlock.openTime, timezone) : null;
                const originalOpenTime = originalBlock.openTime ? timeToDbFormat(originalBlock.openTime, timezone) : null;
                const currentClosedTime = currentBlock.closedTime ? timeToDbFormat(currentBlock.closedTime, timezone) : null;
                const originalClosedTime = originalBlock.closedTime ? timeToDbFormat(originalBlock.closedTime, timezone) : null;

                if (currentOpenTime !== originalOpenTime || currentClosedTime !== originalClosedTime) {
                  toUpdate.push({
                    id: currentBlock.id,
                    input: {
                      is_open: true,
                      open_time: currentOpenTime,
                      closed_time: currentClosedTime,
                      updated_by: userName,
                      updated_at: 'now()',
                    },
                  });
                }
              }
            }
          }

          // Blocks to delete
          const currentBlockIds = new Set(currentBlocks.map((b) => b.id).filter(Boolean));
          const removedBlocks = originalBlocks.filter(
            (b) => b.id && !b.isDefault && !currentBlockIds.has(b.id)
          );
          for (const block of removedBlocks) {
            if (block.id) {
              // Hard-delete for both entity types. This covers user-deleted
              // blocks AND blocks absorbed by mergeTimeBlocks (their ids fell out
              // of currentBlockIds); the surviving merged block was updated above.
              toDelete.push({ id: block.id });
            }
          }
        }
      }

      // Phase 2 — Execute all mutations in parallel
      const promises: Promise<any>[] = [];
      if (toCreate.length > 0) {
        promises.push(onCreate(toCreate));
      }
      for (const u of toUpdate) {
        promises.push(onUpdate({ variables: { id: u.id, input: u.input } }));
      }
      for (const d of toDelete) {
        promises.push(onDelete({ variables: { id: d.id } }));
      }
      await Promise.all(promises);

      // Phase 3 — Refetch once
      onRefetch?.();

      toast.success(`${labels.title} updated successfully`);
      setHasChanges(false);
      setSelectedDays(new Set());
      setIsEditing(false);
      setIsPreviewing(false);
    } catch (error) {
      console.error('Error saving schedule:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to save ${labels.title.toLowerCase()}: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  // Keep hasChanges in sync with pendingDays
  React.useEffect(() => {
    setHasChanges(pendingDays.size > 0);
  }, [pendingDays]);

  // Hold a reload while there are unsaved hours, and while a save runs.
  useReloadGuard(hasChanges || saving, 'Schedule');

  const configuredCount = dayHours.filter((d) => !d.isClosed && d.timeBlocks.length > 0).length;
  const panelOpen = selectedDays.size > 0;

  // Build override lookup map
  const overrideMap = React.useMemo(() => {
    const map = new Map<number, DayOverride>();
    if (currentWeekOverrides) {
      for (const o of currentWeekOverrides) {
        map.set(o.dayOfWeek, o);
      }
    }
    return map;
  }, [currentWeekOverrides]);

  const overrideCount = overrideMap.size;

  // Build selected day names for header
  const selectedDayNames = WEEK_ORDER
    .filter((d) => selectedDays.has(d))
    .map((d) => DAY_NAMES[d]);

  if (loading && dayHours.length === 0) {
    return (
      <Paper sx={{ p: 3, borderRadius: '12px' }}>
        <Typography variant="h6" gutterBottom>
          {labels.title}
        </Typography>
        <LinearProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, borderRadius: '12px' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {labels.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {configuredCount} of 7 days configured{overrideCount > 0 && ` (${overrideCount} override${overrideCount > 1 ? 's' : ''} this week)`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Timeline (default) view buttons */}
          {!isEditing && !isPreviewing && (
            <IconButton
              size="small"
              onClick={() => {
                setIsEditing(true);
                setIsCustomMultiSelect(false);
                const today = new Date().getDay();
                setSelectedDays(new Set([today]));
              }}
            >
              <BiPencil size={16} />
            </IconButton>
          )}

          {/* Edit mode buttons: Cancel | Preview | Save */}
          {isEditing && (
            <>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  if (hasChanges) {
                    // Revert all changes
                    const originalCopy = originalHours.map((d) => ({
                      ...d,
                      timeBlocks: d.timeBlocks.map((b) => ({ ...b })),
                    }));
                    setDayHours(originalCopy);
                    setHasChanges(false);
                  }
                  setIsEditing(false);
                  setIsCustomMultiSelect(false);
                  setSelectedDays(new Set());
                }}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Cancel
              </Button>
              {hasChanges && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<BiShow />}
                  onClick={() => {
                    setIsEditing(false);
                    setIsPreviewing(true);
                    setSelectedDays(new Set());
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Preview
                </Button>
              )}
              {hasChanges && (
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  size="small"
                  sx={{ textTransform: 'none', minWidth: 100 }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </>
          )}

          {/* Preview mode buttons: Back to Edit | Save */}
          {isPreviewing && (
            <>
              <Button
                variant="text"
                size="small"
                startIcon={<BiArrowBack />}
                onClick={() => {
                  setIsPreviewing(false);
                  setIsEditing(true);
                  const today = new Date().getDay();
                  setSelectedDays(new Set([today]));
                }}
                sx={{ textTransform: 'none' }}
              >
                Back to Edit
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                size="small"
                sx={{ textTransform: 'none', minWidth: 100 }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Preview banner */}
      {isPreviewing && (
        <Box sx={{ mb: 1.5, p: 1.5, borderRadius: '8px', bgcolor: '#eef4fb', border: '1px solid #4068a0' }}>
          <Typography variant="body2" sx={{ color: '#2a446d', fontWeight: 500 }}>
            Preview — showing unsaved changes ({pendingDays.size} day{pendingDays.size !== 1 ? 's' : ''} modified)
          </Typography>
        </Box>
      )}

      {/* Timeline View (read-only or preview) */}
      {!isEditing && (
        <WeeklyTimeline
          dayHours={dayHours}
          overrideMap={overrideMap}
          timezone={timezone}
          closedLabel={labels.closed}
          onDayClick={isPreviewing ? undefined : handleTimelineRowClick}
          pendingDays={isPreviewing ? pendingDays : undefined}
        />
      )}

      {/* Edit Mode: Quick group selectors + Day Grid + Edit Panel */}
      {isEditing && (
      <>
      {/* Quick group selectors */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        {QUICK_GROUPS.map((group) => {
          const isActive = !isCustomMultiSelect && group.days.every((d) => selectedDays.has(d)) && selectedDays.size === group.days.length;
          return (
            <Chip
              key={group.label}
              label={group.label}
              size="small"
              variant={isActive ? 'filled' : 'outlined'}
              onClick={() => handleGroupSelect(group.days)}
              sx={{
                cursor: 'pointer',
                borderRadius: '6px',
                ...(isActive && {
                  bgcolor: '#4068a0',
                  color: '#fff',
                  '&:hover': { bgcolor: '#355687' },
                }),
              }}
            />
          );
        })}
        <Chip
          label="Multi-select"
          size="small"
          variant={isCustomMultiSelect ? 'filled' : 'outlined'}
          onClick={() => {
            setIsCustomMultiSelect((prev) => !prev);
            if (!isCustomMultiSelect) {
              // Entering custom mode — clear selection so user starts fresh
              setSelectedDays(new Set());
            }
          }}
          sx={{
            cursor: 'pointer',
            borderRadius: '6px',
            ...(isCustomMultiSelect && {
              bgcolor: '#4068a0',
              color: '#fff',
              '&:hover': { bgcolor: '#355687' },
            }),
          }}
        />
      </Box>

      {/* Day Grid — 7 cells in a row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 1,
        }}
      >
        {WEEK_ORDER.map((dayIndex) => {
          const day = dayHours.find((d) => d.dayOfWeek === dayIndex);
          if (!day) return null;

          const override = overrideMap.get(dayIndex);
          const isAvailable = !day.isClosed && day.timeBlocks.length > 0;
          const isUnavailable = day.isClosed;
          const isSelected = selectedDays.has(dayIndex);

          // For overridden days, use the override's effective state
          const effectiveAvailable = override ? override.isAvailable : isAvailable;
          const effectiveUnavailable = override ? !override.isAvailable : isUnavailable;

          let timeSummary = '';
          if (override) {
            timeSummary = override.isAvailable ? override.formattedTimeRange : 'Off';
          } else if (isAvailable && day.timeBlocks.length > 0) {
            const firstBlock = day.timeBlocks[0];
            timeSummary = formatTimeRange(
              timeToDbFormat(firstBlock.openTime, timezone),
              timeToDbFormat(firstBlock.closedTime, timezone),
              timezone
            );
          }

          const cellContent = (
            <Box
              key={dayIndex}
              onClick={() => handleDayClick(dayIndex)}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 1.5,
                px: 0.5,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 200ms ease-in-out',
                border: '2px solid',
                borderStyle: override ? 'dashed' : 'solid',
                borderColor: isSelected
                  ? '#4068a0'
                  : override
                    ? '#e68a00'
                    : effectiveAvailable
                      ? '#20b020'
                      : effectiveUnavailable
                        ? '#f02020'
                        : 'grey.200',
                bgcolor: isSelected
                  ? '#4068a0'
                  : effectiveAvailable
                    ? 'success.50'
                    : effectiveUnavailable
                      ? 'error.50'
                      : 'grey.50',
                color: isSelected ? '#fff' : 'text.primary',
                '&:hover': {
                  boxShadow: '0px 4px 12px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.04)',
                  transform: 'translateY(-1px)',
                },
                minHeight: 72,
              }}
            >
              {/* Pending changes dot */}
              {pendingDays.has(dayIndex) && !isSelected && (
                <Tooltip title="Unsaved changes" placement="top" arrow>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 5,
                      left: 5,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#f4b020',
                      boxShadow: '0 0 0 2px #fff9f3',
                    }}
                  />
                </Tooltip>
              )}
              {/* Selection checkmark */}
              {isSelected && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 3,
                    right: 3,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BiCheck size={12} color="#fff" />
                </Box>
              )}

              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  color: isSelected ? '#fff' : 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: 11,
                }}
              >
                {DAY_ABBREVS[dayIndex]}
              </Typography>
              {effectiveAvailable ? (
                <>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      color: isSelected ? '#fff' : 'text.primary',
                      mt: 0.25,
                      fontSize: 13,
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    {timeSummary}
                  </Typography>
                  {!override && day.timeBlocks.length > 1 && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: isSelected ? '#fff' : 'text.secondary',
                      }}
                    >
                      +{day.timeBlocks.length - 1} more
                    </Typography>
                  )}
                </>
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    color: isSelected ? '#fff' : 'text.primary',
                    mt: 0.25,
                    fontSize: 13,
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {effectiveUnavailable ? (override ? 'Off' : labels.closed) : 'Not set'}
                </Typography>
              )}
              {/* Status dot */}
              {!isSelected && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    mt: 0.5,
                    bgcolor: override
                      ? '#e68a00'
                      : effectiveAvailable
                        ? '#20b020'
                        : effectiveUnavailable
                          ? '#f02020'
                          : 'grey.300',
                  }}
                />
              )}
            </Box>
          );

          if (override) {
            return (
              <Tooltip
                key={dayIndex}
                title={
                  <Box>
                    <Typography variant="caption" fontWeight={600}>{override.name}</Typography>
                    <Typography variant="caption" display="block">{override.date}</Typography>
                    <Typography variant="caption" display="block">
                      {override.isAvailable ? override.formattedTimeRange : 'Off'}
                    </Typography>
                    {isAvailable && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
                        Base: {formatTimeRange(
                          timeToDbFormat(day.timeBlocks[0]?.openTime, timezone),
                          timeToDbFormat(day.timeBlocks[0]?.closedTime, timezone),
                          timezone
                        )}
                      </Typography>
                    )}
                  </Box>
                }
                arrow
                placement="top"
              >
                {cellContent}
              </Tooltip>
            );
          }

          return cellContent;
        })}
      </Box>

      {/* Edit Panel — slides in below when days are selected */}
      <Collapse in={panelOpen}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box
            sx={{
              mt: 2,
              p: 2.5,
              borderRadius: '10px',
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Panel Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedDays.size === 1
                      ? selectedDayNames[0]
                      : `Editing ${selectedDays.size} days`}
                  </Typography>
                  {selectedDays.size > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedDayNames.join(', ')}
                    </Typography>
                  )}
                </Box>
                {/* Reset changes for selected day(s) */}
                {selectedDays.size === 1 && pendingDays.has(Array.from(selectedDays)[0]) && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<BiUndo size={14} />}
                    onClick={() => handleResetDay(Array.from(selectedDays)[0])}
                    sx={{ textTransform: 'none', color: 'text.secondary', fontSize: 12 }}
                  >
                    Reset
                  </Button>
                )}
              </Box>

              {/* Availability toggle — check / X icons */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  Available?
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleSetAvailable}
                  sx={{
                    width: 36,
                    height: 36,
                    border: '2px solid',
                    borderColor: !editIsClosed ? '#20b020' : 'grey.300',
                    bgcolor: !editIsClosed ? '#e8f5e8' : 'transparent',
                    color: !editIsClosed ? '#20b020' : 'grey.400',
                    '&:hover': {
                      bgcolor: !editIsClosed ? '#d4ecd4' : 'grey.100',
                    },
                  }}
                >
                  <BiCheck size={20} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleSetUnavailable}
                  sx={{
                    width: 36,
                    height: 36,
                    border: '2px solid',
                    borderColor: editIsClosed ? '#f02020' : 'grey.300',
                    bgcolor: editIsClosed ? '#fff2f4' : 'transparent',
                    color: editIsClosed ? '#f02020' : 'grey.400',
                    '&:hover': {
                      bgcolor: editIsClosed ? '#ffe0e4' : 'grey.100',
                    },
                  }}
                >
                  <BiX size={20} />
                </IconButton>
              </Box>
            </Box>

            {/* Override info banner */}
            {(() => {
              const selectedOverrides = Array.from(selectedDays)
                .map((d) => overrideMap.get(d))
                .filter(Boolean) as DayOverride[];
              if (selectedOverrides.length === 0) return null;
              const o = selectedOverrides[0];
              return (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: '8px',
                    bgcolor: '#fff8e6',
                    border: '1px solid #e68a00',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#8a5300' }}>
                    {DAY_NAMES[o.dayOfWeek]} has an override ({o.name}, {o.date}).
                    You are editing the recurring weekly schedule.
                  </Typography>
                </Box>
              );
            })()}

            {/* Unavailable message */}
            {editIsClosed && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {selectedDays.size === 1
                  ? labels.unavailableText(selectedDayNames[0])
                  : `${selectedDayNames.join(', ')} will be marked as unavailable.`}
              </Typography>
            )}

            {/* Available — show quick presets + time blocks */}
            {!editIsClosed && (
              <>
                {/* Quick Presets */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Quick Set
                  </Typography>
                  {PRESETS.map((preset) => (
                    <Chip
                      key={preset.label}
                      label={preset.label}
                      size="small"
                      variant="outlined"
                      onClick={() => handleApplyPreset(preset.open, preset.close)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: '6px',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    />
                  ))}
                </Box>

                {/* Time Blocks */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Time Blocks
                  </Typography>
                  {editBlocks.map((block, blockIdx) => (
                    <Box
                      key={blockIdx}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr auto',
                        gap: 1.5,
                        alignItems: 'end',
                        bgcolor: '#fff',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px',
                        p: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          {labels.openLabel}
                        </Typography>
                        <TimePicker
                          value={block.openTime}
                          onChange={(value) => handleTimeBlockChange(blockIdx, 'openTime', value)}
                          slotProps={{
                            textField: { size: 'small', fullWidth: true },
                          }}
                        />
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          {labels.closeLabel}
                        </Typography>
                        <TimePicker
                          value={block.closedTime}
                          onChange={(value) => handleTimeBlockChange(blockIdx, 'closedTime', value)}
                          slotProps={{
                            textField: { size: 'small', fullWidth: true },
                          }}
                        />
                      </Box>

                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteTimeBlock(blockIdx)}
                        sx={{ mb: 0.5 }}
                      >
                        <BiTrash />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<BiPlus />}
                    onClick={handleAddTimeBlock}
                    sx={{
                      textTransform: 'none',
                      borderStyle: 'dashed',
                      borderWidth: 2,
                      py: 1,
                      '&:hover': {
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    Add Time Block
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </LocalizationProvider>
      </Collapse>
      </>
      )}
    </Paper>
  );
};

export default ScheduleEditor;
